import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getDjangoEndpoint } from "src/utils/apiEndpoints";
import { toApiError } from "src/types/api";
import axiosInstance from "src/store/features/auth/axiosCSRF";
interface AssayResponse {
    s3_path: string | null;
    exists: boolean;
    status?: number;
    statusText?: string;
    file_size?: number | null;
    valid?: boolean;
}

export interface iAssayCheckState {
    group_name: string | null;
    library_name: string | null;
    smile_column_name: string | null;
    affinity_column: string | null;
    compound_column_name: string | null; 
    activity_column: string | null; 
    workflow_name : string | null; 
    round: number | null;
    file_name: string | null; 
    file_ext: string | null; 
    isLoading: boolean;
    message: string | null;
    response: AssayResponse | null;
    assayFileCheckResponse: AssayResponse | null;
    isSuccess: boolean;
    isError: boolean;
    lastRequestParams: any;
}


const initialState: iAssayCheckState = {
    group_name: null,
    library_name: null,
    smile_column_name: null,
    affinity_column: null,
    compound_column_name: null,
    activity_column: null,
    workflow_name : null,
    round: null,
    file_name: null,
    file_ext: null,
    isLoading: false,
    message: null,
    response: null, // null로 시작
    assayFileCheckResponse: null,
    isSuccess: false,
    isError: false,
    lastRequestParams: null,
};

// Assay Upload API 호출
export const uploadAssayCheckAPI = createAsyncThunk(
    "ASSAY_UPLOAD_CHECK",
    async (params: { 
        group_name: string; 
        library_name: string; 
        smile_column_name: string; 
        affinity_column: string;
        compound_column_name: string;
        activity_column: string;
        workflow_name : string;
        round: number;
        file_name: string;
        file_ext: string;
    }, { rejectWithValue }) => {
        try {
            const djangoEndpoint = getDjangoEndpoint();
            const pathParam = '/upload/assay-check/';
            
            if (!djangoEndpoint) {
                throw new Error("Django endpoint not found");
            }
            
            // workflow_name에 따라 payload 구성
            const payload: any = {
                group_name: params.group_name,
                library_name: params.library_name,
                smile_column_name: params.smile_column_name,
                affinity_column: params.affinity_column,
                compound_column_name: params.compound_column_name,
                workflow_name : params.workflow_name,
                round: params.round,
                file_name: params.file_name,
                file_ext: params.file_ext
            };
            
            // kinase inhibitor일 때만 activity_column 추가
            if (params.workflow_name === 'kinase inhibitor') {
                payload.activity_column = params.activity_column;
            }
            
            const response = await axiosInstance.post(`${djangoEndpoint}${pathParam}`, payload, {
                withCredentials: true,
            });
            
            // 응답에 요청 파라미터와 HTTP 상태 정보 추가
            return {
                ...response.data,
                status: response.status,
                statusText: response.statusText,
                // 요청 파라미터들도 함께 반환
                requestParams: params
            };
        } catch (error) {
            console.error('uploadAssayCheckAPI error:', error);

            const apiError = toApiError(error);
            const errorStatus = apiError.response?.status || 500;
            const errorMessage = apiError.response?.data || apiError.message;
            
            return rejectWithValue({
                status: errorStatus,
                message: errorMessage,
                requestParams: params // 에러 시에도 요청 파라미터 포함
            });
        }
    }
);

// Assay Upload Slice
export const uploadAssayCheckSlice = createSlice({
    name: "uploadAssayCheck",
    initialState,
    reducers: {
        // 전체 초기화
        resetAssayCheck: (state) => {
            Object.assign(state, initialState);
        },
        
        // 부분 초기화 
        resetAssayResponse: (state) => {
            state.response = null;
            state.assayFileCheckResponse = null;
            state.message = null;
            state.isSuccess = false;
            state.isError = false;
            state.isLoading = false;
        },
        
        // 파라미터만 설정 (응답은 유지)
        setAssayParams: (state, action: PayloadAction<Partial<iAssayCheckState>>) => {
            // 기존 상태를 유지하면서 새로운 파라미터만 업데이트
            Object.assign(state, action.payload);
        },
        
        // 개별 파라미터 설정 액션들 추가
        setAssayGroupAndLibrary: (state, action: PayloadAction<{ group_name: string; library_name: string }>) => {
            state.group_name = action.payload.group_name;
            state.library_name = action.payload.library_name;
        },
        
        setAssayColumns: (state, action: PayloadAction<{ 
            smile_column_name?: string; 
            affinity_column?: string; 
            compound_column_name?: string;
            activity_column?: string;
        }>) => {
            if (action.payload.smile_column_name !== undefined) {
                state.smile_column_name = action.payload.smile_column_name;
            }
            if (action.payload.affinity_column !== undefined) {
                state.affinity_column = action.payload.affinity_column;
            }
            if (action.payload.compound_column_name !== undefined) {
                state.compound_column_name = action.payload.compound_column_name;
            }
            if (action.payload.activity_column !== undefined) {
                state.activity_column = action.payload.activity_column;
            }
        },
        
        setAssayFileInfo: (state, action: PayloadAction<{ 
            file_name: string; 
            file_ext: string; 
            round?: number;
        }>) => {
            state.file_name = action.payload.file_name;
            state.file_ext = action.payload.file_ext;
            if (action.payload.round !== undefined) {
                state.round = action.payload.round;
            }
        },
        
        // 에러 상태만 초기화
        clearAssayError: (state) => {
            state.isError = false;
            state.message = null;
        },
        
        // 로딩 상태 설정
        setAssayLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(uploadAssayCheckAPI.pending, (state, action) => {
                // 기존 응답들은 유지하고 로딩 상태만 업데이트
                state.isLoading = true;
                state.message = null;
                state.isSuccess = false;
                state.isError = false;
                
                // 요청 파라미터 저장 (나중에 참조 가능)
                state.lastRequestParams = action.meta.arg;
            })
            .addCase(uploadAssayCheckAPI.fulfilled, (state, action) => {
                // 기존 상태들을 유지하면서 응답만 업데이트
                state.isLoading = false;
                state.isSuccess = true;
                state.isError = false;
                state.message = null;
                
                // 응답 데이터 저장
                const responseData: AssayResponse = {
                    s3_path: action.payload.s3_path || null,
                    exists: action.payload.exists || false,
                    status: action.payload.status,
                    statusText: action.payload.statusText,
                    file_size: action.payload.file_size || null,
                    valid: action.payload.valid || false,
                };
                
                // 두 곳에 모두 저장 
                state.response = responseData;
                state.assayFileCheckResponse = responseData;
                
                // 요청 파라미터들을 상태에 저장
                if (action.payload.requestParams) {
                    const params = action.payload.requestParams;
                    state.group_name = params.group_name;
                    state.library_name = params.library_name;
                    state.smile_column_name = params.smile_column_name;
                    state.affinity_column = params.affinity_column;
                    state.compound_column_name = params.compound_column_name;
                    state.activity_column = params.activity_column;
                    state.workflow_name = params.workflow_name;
                    state.round = params.round;
                    state.file_name = params.file_name;
                    state.file_ext = params.file_ext;
                }
            })
            .addCase(uploadAssayCheckAPI.rejected, (state, action) => {
                // 에러 시에도 기존 설정들은 유지
                state.isLoading = false;
                state.isSuccess = false;
                state.isError = true;
                
                const errorPayload = action.payload as any;
                state.message = errorPayload?.message || "Assay check failed";
                
                // 응답만 에러 상태로 설정 
                const errorResponse: AssayResponse = {
                    s3_path: null,
                    exists: false,
                    status: errorPayload?.status || 500,
                    statusText: "Error",
                    file_size: null,
                    valid: false,
                };
                
                state.response = errorResponse;
                state.assayFileCheckResponse = errorResponse;
                
                // 에러 시에도 요청 파라미터는 저장 
                if (errorPayload?.requestParams) {
                    const params = errorPayload.requestParams;
                    state.group_name = params.group_name;
                    state.library_name = params.library_name;
                    state.smile_column_name = params.smile_column_name;
                    state.affinity_column = params.affinity_column;
                    state.compound_column_name = params.compound_column_name;
                    state.activity_column = params.activity_column;
                    state.workflow_name = params.workflow_name;
                    state.round = params.round;
                    state.file_name = params.file_name;
                    state.file_ext = params.file_ext;
                }
                
                console.error('Assay check API rejected:', errorPayload);
            });
    },
});

export const { 
    resetAssayCheck, 
    resetAssayResponse,
    setAssayParams,
    setAssayGroupAndLibrary,
    setAssayColumns,
    setAssayFileInfo,
    clearAssayError,
    setAssayLoading
} = uploadAssayCheckSlice.actions;

export default uploadAssayCheckSlice.reducer;


// Assay 상태 확인을 위한 셀렉터 헬퍼
export const getAssayCheckStatus = (state: { uploadAssayCheck: iAssayCheckState }) => {
    const assayState = state.uploadAssayCheck;
    return {
        isLoading: assayState.isLoading,
        isSuccess: assayState.isSuccess,
        isError: assayState.isError,
        hasValidResponse: assayState.response?.exists && assayState.response?.valid,
        responseExists: assayState.response?.exists || false,
        message: assayState.message
    };
};

// Assay 파라미터 완성도 확인 - workflow에 따라 동적으로 체크
export const getAssayParamsCompleteness = (state: { uploadAssayCheck: iAssayCheckState }) => {
    const assayState = state.uploadAssayCheck;
    
    // 기본 필수 파라미터
    const baseRequiredParams = [
        'group_name',
        'library_name', 
        'smile_column_name',
        'affinity_column',
        'compound_column_name',
        'workflow_name',
        'file_name',
        'file_ext'
    ];
    
    // workflow_name이 kinase inhibitor일 때만 activity_column 추가
    const requiredParams = assayState.workflow_name === 'kinase inhibitor'
        ? [...baseRequiredParams, 'activity_column']
        : baseRequiredParams;
    
    const missingParams = requiredParams.filter(param => !assayState[param as keyof iAssayCheckState]);
    
    return {
        isComplete: missingParams.length === 0,
        missingParams,
        completeness: ((requiredParams.length - missingParams.length) / requiredParams.length) * 100
    };
};